import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import MainLayout from './components/MainLayout';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Questionari from './pages/Questionari';
import Questionnaire from './pages/Questionnaire';
import UsersManagement from './pages/UsersManagement';
import UserPanel from './pages/UserPanel';
import Statistics from './pages/Statistics';
import ImportQuestionnaires from './pages/ImportQuestionnaires';
import GuidaRapida from './pages/GuidaRapida';
import Profilo from './pages/Profilo';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import LegalAcceptance from './pages/LegalAcceptance';
import axios from 'axios';
import './App.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Hook to remove Emergent badge
const useRemoveEmergentBadge = () => {
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.remove();
      }
    };
    removeBadge();
    // Also observe for dynamically added badge
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
};

const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false, skipAcceptanceCheck = false }) => {
  const { user, loading, token } = useAuth();
  const [needsAcceptance, setNeedsAcceptance] = useState(null);
  const [checkingAcceptance, setCheckingAcceptance] = useState(true);

  useEffect(() => {
    const checkAcceptance = async () => {
      if (!user || !token || skipAcceptanceCheck) {
        setCheckingAcceptance(false);
        setNeedsAcceptance(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/api/legal/acceptance-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNeedsAcceptance(response.data.needs_acceptance);
      } catch (error) {
        console.error('Error checking acceptance status:', error);
        setNeedsAcceptance(false);
      } finally {
        setCheckingAcceptance(false);
      }
    };
    
    if (user && token) {
      checkAcceptance();
    } else {
      setCheckingAcceptance(false);
    }
  }, [user, token, skipAcceptanceCheck]);

  if (loading || checkingAcceptance) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F5FBF9]" role="status" aria-label="Caricamento">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BA99C]" aria-hidden="true"></div>
        <span className="sr-only">Caricamento in corso...</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOnly && user.role !== 'admin' && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect to acceptance page if needed (but NOT if skipAcceptanceCheck is true)
  if (!skipAcceptanceCheck && needsAcceptance) {
    return <Navigate to="/accettazione-documenti" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F5FBF9]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BA99C]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  useRemoveEmergentBadge();
  
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Skip link for keyboard navigation */}
        <a href="#main-content" className="skip-link">
          Vai al contenuto principale
        </a>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Legal Acceptance Page - Protected but skip acceptance check to avoid infinite loop */}
          <Route
            path="/accettazione-documenti"
            element={
              <ProtectedRoute skipAcceptanceCheck>
                <LegalAcceptance />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/questionari" element={<Questionari />} />
            <Route path="/questionario/:id" element={<Questionnaire />} />
            <Route path="/importa-questionari" element={<ImportQuestionnaires />} />
            <Route path="/guida" element={<GuidaRapida />} />
            <Route
              path="/utenti"
              element={
                <ProtectedRoute adminOnly>
                  <UsersManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pannello-utenti"
              element={
                <ProtectedRoute superAdminOnly>
                  <UserPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/statistiche"
              element={<Statistics />}
            />
            <Route
              path="/profilo"
              element={<Profilo />}
            />
            <Route
              path="/privacy-policy"
              element={<PrivacyPolicy />}
            />
            <Route
              path="/termini-condizioni"
              element={<TermsConditions />}
            />
          </Route>

          {/* Redirect root to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* 404 */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;